import React, { useEffect, useState } from "react";
import { connect, shallowEqual, useSelector } from "react-redux";
import * as categoryRedux from "modules/Category/_redux/categoryRedux";
import * as advertRedux from "modules/Advert/_redux/advertRedux";
import { formatThousandsDot } from "helpers/FormatNumbersWithComma";
import * as initialEndpoint from "modules/Initial/_redux/initialEndpoint";
import KindFilter from "./KindFilter";
import ListingTypeFilter from "./ListingTypeFilter";
import SelectSection from "./SelectSection";
import MultiSelectSection from "./MultiSelectSection";
import SLBrandModelCascade from "./SLBrandModelCascade";
import SLMinMaxKm from "./SLMinMaxKm";
import SLMinMaxYear from "./SLMinMaxYear";
import SLMinMaxM2 from "./SLMinMaxM2";
import SLMinMaxKatSayisi from "./SLMinMaxKatSayisi";
import SLBulunduguKatRange from "./SLBulunduguKatRange";
import FilterAccordion from "./FilterAccordion";
import "./sahibindenFilters.css";

const getFilterSection = (filter) => ({
  multiSelect: <MultiSelectSection filter={filter} />,
  singleSelect: <SelectSection filter={filter} />,
  // multiCheck artık sahibinden tarzı sol-hizalı checkbox listesi ile çizilir.
  multiCheck: <MultiSelectSection filter={filter} />,
  brandModelCascade: <SLBrandModelCascade filter={filter} />,
  kmInput: <SLMinMaxKm filter={filter} />,
  minMaxKm: <SLMinMaxKm filter={filter} />,
  yearInput: <SLMinMaxYear filter={filter} />,
  minMaxYear: <SLMinMaxYear filter={filter} />,
  m2Input: <SLMinMaxM2 filter={filter} />,
  minMaxM2: <SLMinMaxM2 filter={filter} />,
  katSayisiInput: <SLMinMaxKatSayisi filter={filter} />,
  minMaxKatSayisi: <SLMinMaxKatSayisi filter={filter} />,
  bulunduguKatInput: <SLBulunduguKatRange filter={filter} />,
  minMaxBulunduguKat: <SLBulunduguKatRange filter={filter} />,
});

// Sahibinden gibi: yalnızca birincil filtreler açık, gerisi collapsed başlar.
const PRIMARY_OPEN = new Set([
  "konut-tipi",
  "oda-sayisi",
  "m2",
  "vehicleBrandModel",
  "year",
  "km",
]);

function PricePanel(props) {
  const [prices, setPrices] = useState({ minimumPrice: "", maximumPrice: "" });

  useEffect(() => {
    if (props.selectedFilter.minMaxFiyat) {
      setPrices({
        minimumPrice: props.selectedFilter.minMaxFiyat[0],
        maximumPrice: props.selectedFilter.minMaxFiyat[1],
      });
    } else {
      setPrices({ minimumPrice: "", maximumPrice: "" });
    }
  }, [props.selectedFilter]);

  const changeHandler = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setPrices({
      ...prices,
      [e.target.name]: digits === "" ? "" : Number(digits),
    });
  };

  const applyPrice = () => {
    if (Number(prices.maximumPrice) == 0 && Number(prices.minimumPrice) == 0) {
      props.removeFilter({ parent: "minMaxFiyat" });
    } else {
      props.addFilterBulk({
        parent: "minMaxFiyat",
        value: [Number(prices.minimumPrice), Number(prices.maximumPrice)],
      });
    }
  };

  return (
    <div>
      <div className="sf-minmax">
        <input
          className="sf-input"
          name="minimumPrice"
          type="text"
          inputMode="numeric"
          placeholder="min TL"
          value={formatThousandsDot(prices.minimumPrice)}
          onChange={changeHandler}
          onKeyDown={(e) => e.key === "Enter" && applyPrice()}
        />
        <span className="sf-dash">–</span>
        <input
          className="sf-input"
          name="maximumPrice"
          type="text"
          inputMode="numeric"
          placeholder="max TL"
          value={formatThousandsDot(prices.maximumPrice)}
          onChange={changeHandler}
          onKeyDown={(e) => e.key === "Enter" && applyPrice()}
        />
      </div>
      <button type="button" className="fp-apply" onClick={applyPrice}>
        Ara
      </button>
    </div>
  );
}

const ConnectedPricePanel = connect(
  (state) => ({ selectedFilter: state.category.selectedFilter }),
  { ...categoryRedux.actions, ...advertRedux.actions }
)(PricePanel);

function FilterPanel(props) {
  const { selectedFilter, filters } = useSelector(
    (state) => ({
      selectedFilter: state.category.selectedFilter,
      filters: state.category.categoryDetail.filters,
    }),
    shallowEqual
  );

  const { provinces } = props;
  const selectedProvince = selectedFilter?.location?.province;
  const selectedDistrict = selectedFilter?.location?.district;

  const [neighborhoods, setNeighborhoods] = useState([]);

  useEffect(() => {
    if (!selectedProvince || !selectedDistrict) {
      setNeighborhoods([]);
      return;
    }
    const plate = provinces?.find(
      (province) => province.name == selectedProvince.name
    )?.plate;
    if (!plate) {
      setNeighborhoods([]);
      return;
    }
    initialEndpoint
      .getNeighborhoods(plate, selectedDistrict.name)
      .then((res) => res.data)
      .then((result) => setNeighborhoods(result.neighborhoods || []));
  }, [selectedProvince, selectedDistrict, provinces]);

  return (
    <div className="fp-panel">
      <FilterAccordion title="İlan Türü">
        <KindFilter />
      </FilterAccordion>

      <FilterAccordion title="İşlem Türü">
        <ListingTypeFilter />
      </FilterAccordion>

      <FilterAccordion title="Adres">
        <SelectSection
          filter={{
            filterType: "singleSelect",
            values: provinces.map((province) => province.name),
            title: "İl",
            slug: "province",
          }}
          extraData={provinces}
        />
        {selectedProvince ? (
          <SelectSection
            filter={{
              filterType: "singleSelect",
              values: provinces
                ?.find((province) => province.name == selectedProvince.name)
                ?.districts?.map((district) => district.name),
              title: "İlçe",
              slug: "district",
            }}
            extraData={provinces}
          />
        ) : null}
        {selectedDistrict && neighborhoods.length > 0 ? (
          <SelectSection
            filter={{
              filterType: "singleSelect",
              values: neighborhoods,
              title: "Semt / Mahalle",
              slug: "neighborhood",
            }}
            extraData={provinces}
          />
        ) : null}
      </FilterAccordion>

      <FilterAccordion title="Fiyat">
        <ConnectedPricePanel />
      </FilterAccordion>

      {(filters || []).map((filter) => (
        <FilterAccordion
          key={filter.slug}
          title={filter.title}
          defaultOpen={PRIMARY_OPEN.has(filter.slug)}
        >
          {getFilterSection(filter)[filter.filterType]}
        </FilterAccordion>
      ))}
    </div>
  );
}

export default FilterPanel;
